import React, { type ReactNode } from "react";
import { AspectRatio, type AspectRatioProps, Flex, type FlexProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { type ChipProps } from "./chips";
import { TextTitle, type TextTitleProps } from "./text";

type BaseDiptychProps = {
  /**
   * Optional chip element to display above the title.
   */
  chip?: {
    text: string;
    variant?: ChipProps["variant"];
  };

  /**
   * The title text.
   */
  title: TextTitleProps["title"];

  /**
   * The html element to use for the title.
   *
   * @default "h2"
   */
  titleAs?: TextTitleProps["titleAs"];

  /**
   * The description text.
   */
  description?: TextTitleProps["description"];

  /**
   * Optional CTA buttons or links.
   */
  actions?: TextTitleProps["actions"];

  /**
   * Media to display beside the text. Should be either an image or lottie animation
   */
  media: ReactNode;

  /**
   * Optional media aspect ratio.
   */
  mediaRatio?: AspectRatioProps["ratio"];

  /**
   * The direction of the media
   */
  direction: "left" | "right";

  /**
   * The size of the media
   *
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * The color theme of the diptych.
   *
   * @default "light"
   */
  theme?: Theme;
};

export type DiptychProps = BaseDiptychProps & Omit<FlexProps, "direction">;

export function Diptych({
  chip,
  title,
  titleAs,
  description,
  actions,
  media,
  mediaRatio,
  direction,
  gap,
  maxW,
  size = "md",
  theme = "light",
  ...props
}: DiptychProps) {
  return (
    <Flex
      align="center"
      flexDir={{ base: "column", md: direction === "right" ? "row-reverse" : "row" }}
      gap={gap ?? { base: "biggest", lg: size === "lg" ? "72" : "huge", xl: size === "lg" ? "88px" : "136px" }}
      justifyContent={{ base: "center", md: "space-between" }}
      maxW={maxW ?? { base: "100%", lg: size === "sm" ? "88%" : "100%" }}
      mx="auto"
      w="100%"
      {...props}
    >
      <AspectRatio
        borderRadius={{ base: "small", md: "bigger" }}
        ratio={mediaRatio ?? (size === "lg" ? 872 / 580 : 1 / 1)}
        flex={1}
        w={{ base: "100%", md: size === "lg" ? "66%" : "50%" }}
        maxW={{ base: "mediaMaxW.mobile", md: "100%", lg: size === "lg" ? "872px" : "648px" }}
        pos="relative"
        overflow="hidden"
      >
        {media}
      </AspectRatio>

      <Flex direction="column" flex={1} mx="auto">
        <TextTitle
          theme={theme}
          chip={chip}
          title={title}
          titleAs={titleAs}
          description={description}
          actions={actions}
          size={size === "lg" ? "xs" : size}
        />
      </Flex>
    </Flex>
  );
}
